import type { Pending } from "@/api/books";
import { formatDate } from "@/lib/format";

/* Links between the books and the records they are derived from, both ways. */

export type SourceRef = {
  source_kind: string | null;
  source_id: string | null;
  source_invoice_id: string | null;
};

/** The page a record lives on, or null for a manual entry. A payment lives on its invoice. */
export function recordHref({ source_kind, source_id, source_invoice_id }: SourceRef): string | null {
  if (!source_kind || !source_id) return null;
  if (source_invoice_id) return `/invoices/${source_invoice_id}`;
  if (source_kind === "expense" || source_kind === "expense_paid") return "/expenses";
  if (source_kind === "transfer") return "/money?tab=transfers";
  if (source_kind === "opening") return "/money";
  return null;
}

/** The Journals tab narrowed to one record's entries. */
export function entriesHref(kind: string, id: string): string {
  return `/books?tab=journals&source=${kind}:${id}`;
}

const SOURCE = /^([a-z_]+):([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

export function parseSource(value: string | null): { kind: string; id: string } | null {
  const match = SOURCE.exec(value ?? "");
  return match ? { kind: match[1]!, id: match[2]! } : null;
}

/** The Exchange rates tab with a new rate for that currency and day started. */
export function rateHref(currency: string, on: string): string {
  return `/books?tab=rates&currency=${currency}&on=${on}`;
}

/** Why a record is not in the books yet, in plain words. */
export function pendingWords(row: Pending): string {
  if (row.reason === "rate_missing") {
    return `Waiting for a ${row.currency ?? ""} rate on or before ${formatDate(row.on)}`;
  }
  if (row.reason === "error") {
    return `Could not be booked: ${row.detail}`;
  }
  return `Waits for ${row.detail}`;
}

/** Where the fix is: a new rate for the currency and day it waits for, or the record itself. */
export function fixHref(row: Pending): string | null {
  if (row.reason === "rate_missing" && row.currency && row.on) {
    return rateHref(row.currency, row.on);
  }
  return recordHref(row);
}

const GROUPS: { title: string; kinds: string[] }[] = [
  { title: "Invoices and credit notes", kinds: ["invoice", "credit_note", "write_off"] },
  { title: "Payments", kinds: ["payment"] },
  { title: "Bills and payouts", kinds: ["expense", "expense_paid"] },
  { title: "Transfers", kinds: ["transfer"] },
  { title: "Opening balances", kinds: ["opening"] },
];

export function groupPending(rows: Pending[]): { title: string; rows: Pending[] }[] {
  return GROUPS.map((group) => ({
    title: group.title,
    rows: rows.filter((row) => group.kinds.includes(row.source_kind)),
  })).filter((group) => group.rows.length > 0);
}
